import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'

// Other components open the modal by dispatching this event on `window`.
export const OPEN_SEARCH_EVENT = 'docs:open-search'

interface SearchEntry {
  kind: 'guide' | 'operation'
  /** What renders as the bold first line. */
  title: string
  /** Subtitle / excerpt rendered below the title (e.g. summary or path). */
  subtitle: string
  /** Free text searched in addition to title/subtitle. */
  keywords: string
  /** Where clicking the entry navigates. Always an in-app router path. */
  href: string
}

interface Hit {
  entry: SearchEntry
  excerptKey: string
}

// Static catalogue of the guide pages declared in App.tsx. Kept in this file
// (rather than imported from a separate registry) because the docs-site has
// no MDX content loader and the guide list is tiny — duplicating it here is
// cheaper than building one.
const GUIDES: SearchEntry[] = [
  {
    kind: 'guide',
    title: 'Overview',
    subtitle: 'Build with the Chosen API — quickstart, auth, what you can do.',
    keywords: 'getting started intro hello world quickstart',
    href: '/',
  },
  {
    kind: 'guide',
    title: 'API reference',
    subtitle: 'Every endpoint, request/response shapes, try-it-now console.',
    keywords:
      'reference openapi scalar endpoints operations try it now playground',
    href: '/reference',
  },
  {
    kind: 'guide',
    title: 'Authentication',
    subtitle: 'Create an API key, send it as a Bearer token, choose a role.',
    keywords:
      'auth login bearer token api key cho_ header authorization rbac role',
    href: '/guides/authentication',
  },
  {
    kind: 'guide',
    title: 'Errors',
    subtitle: 'HTTP status codes, error payload shape, common failures.',
    keywords:
      'error errors 400 401 403 404 422 429 500 retry status code exception',
    href: '/guides/errors',
  },
  {
    kind: 'guide',
    title: 'Rate limits',
    subtitle: 'Per-tier limits, per-domain outreach throttle, the 429 headers.',
    keywords:
      'rate limit throttle 429 quota tier cost expensive cheap_llm outreach',
    href: '/guides/rate-limits',
  },
  {
    kind: 'guide',
    title: 'Versioning',
    subtitle: 'How v1 evolves, when breaking changes ship as v2, the beta gate.',
    keywords: 'version v1 v2 beta breaking change deprecation changelog',
    href: '/guides/versioning',
  },
  {
    kind: 'guide',
    title: 'Unauthenticated public endpoints',
    subtitle: 'The handful of public routes that don\'t need an API key.',
    keywords: 'public unauth no auth careers application apply candidate',
    href: '/guides/public-endpoints',
  },
]

// Minimal OpenAPI 3 typing — only the fields we read.
interface OpenApiSpec {
  paths?: Record<string, Record<string, OpenApiOperation | unknown>>
}
interface OpenApiOperation {
  summary?: string
  description?: string
  tags?: string[]
  operationId?: string
}

const HTTP_METHODS = ['get', 'post', 'put', 'patch', 'delete'] as const

function isOperation(value: unknown): value is OpenApiOperation {
  return typeof value === 'object' && value !== null
}

function operationsFromSpec(spec: OpenApiSpec): SearchEntry[] {
  const entries: SearchEntry[] = []
  for (const [path, byMethod] of Object.entries(spec.paths ?? {})) {
    for (const method of HTTP_METHODS) {
      const op = (byMethod as Record<string, unknown>)[method]
      if (!isOperation(op)) continue
      const summary = op.summary ?? ''
      const tag = op.tags?.[0] ?? ''
      // Skip routes Scalar will hide anyway (e.g. internal-tagged ones that
      // somehow slipped into the public spec — defensive).
      if (tag.startsWith('Internal:')) continue
      entries.push({
        kind: 'operation',
        title: summary || `${method.toUpperCase()} ${path}`,
        subtitle: `${method.toUpperCase()} ${path}`,
        keywords: [
          tag,
          op.operationId ?? '',
          op.description ?? '',
          path,
        ].join(' '),
        // Scalar's react component reads the URL hash for deep-linking;
        // `#tag/<slug>` jumps to a tag group, which is the most reliable
        // anchor since per-operation IDs vary by exporter. Falling back to
        // `/reference` (no hash) when there's no tag is also fine.
        href: tag ? `/reference#tag/${slugifyTag(tag)}` : '/reference',
      })
    }
  }
  return entries
}

function slugifyTag(tag: string): string {
  return tag
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

// One module-level promise so the spec is fetched at most once per page load.
let entriesPromise: Promise<SearchEntry[]> | null = null

function loadEntries(): Promise<SearchEntry[]> {
  if (entriesPromise) return entriesPromise
  entriesPromise = fetch('/openapi.public.json', { credentials: 'omit' })
    .then((r) => (r.ok ? (r.json() as Promise<OpenApiSpec>) : null))
    .then((spec) => (spec ? [...GUIDES, ...operationsFromSpec(spec)] : GUIDES))
    .catch(() => GUIDES)
  return entriesPromise
}

function tokenize(query: string): string[] {
  return query
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\p{L}\p{N}\s/_-]+/gu, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 0)
}

function countOccurrences(haystack: string, needle: string): number {
  if (!needle) return 0
  let count = 0
  let idx = 0
  while ((idx = haystack.indexOf(needle, idx)) !== -1) {
    count += 1
    idx += needle.length
  }
  return count
}

function scoreEntry(entry: SearchEntry, tokens: string[]): number {
  const title = entry.title.toLowerCase()
  const subtitle = entry.subtitle.toLowerCase()
  const keywords = entry.keywords.toLowerCase()
  let score = 0
  let matchedAll = true
  for (const t of tokens) {
    let hit = false
    const a = countOccurrences(title, t)
    if (a > 0) {
      score += 100 * a
      hit = true
    }
    const b = countOccurrences(subtitle, t)
    if (b > 0) {
      score += 30 * b
      hit = true
    }
    const c = countOccurrences(keywords, t)
    if (c > 0) {
      score += 15 * Math.min(c, 4)
      hit = true
    }
    if (!hit) matchedAll = false
  }
  // Phrase bonus.
  const phrase = tokens.join(' ')
  if (phrase.length > 0 && title.includes(phrase)) score += 80
  // Guides are usually more useful starting points than a single operation.
  if (entry.kind === 'guide' && score > 0) score += 5
  return matchedAll ? score : Math.floor(score / 5)
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function highlight(text: string, tokens: string[]): ReactNode {
  if (tokens.length === 0) return text
  const pattern = new RegExp(`(${tokens.map(escapeRegex).join('|')})`, 'gi')
  const parts = text.split(pattern)
  return parts.map((part, i) =>
    i % 2 === 1 ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>,
  )
}

function search(entries: SearchEntry[], query: string, limit = 12): Hit[] {
  const tokens = tokenize(query)
  if (tokens.length === 0) return []
  const scored: Array<{ entry: SearchEntry; score: number }> = []
  for (const entry of entries) {
    const score = scoreEntry(entry, tokens)
    if (score > 0) scored.push({ entry, score })
  }
  scored.sort(
    (a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title),
  )
  return scored.slice(0, limit).map(({ entry }) => ({
    entry,
    // De-dupe key — two operations under the same tag share `href`, so we add
    // the title to keep React keys unique.
    excerptKey: `${entry.href}|${entry.title}`,
  }))
}

export default function SearchModal() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [entries, setEntries] = useState<SearchEntry[] | null>(null)
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((v) => !v)
      } else if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    function onOpen(): void {
      setOpen(true)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener(OPEN_SEARCH_EVENT, onOpen)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener(OPEN_SEARCH_EVENT, onOpen)
    }
  }, [])

  useEffect(() => {
    if (!open || entries) return
    void loadEntries().then(setEntries)
  }, [open, entries])

  useEffect(() => {
    if (!open) {
      setQuery('')
      setActive(0)
      return
    }
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const focusId = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => {
      document.body.style.overflow = prevOverflow
      window.clearTimeout(focusId)
    }
  }, [open])

  const tokens = useMemo(() => tokenize(query), [query])
  const hits = useMemo(
    () => (entries ? search(entries, query) : []),
    [entries, query],
  )

  useEffect(() => {
    setActive((a) => (hits.length === 0 ? 0 : Math.min(a, hits.length - 1)))
  }, [hits.length])

  const go = useCallback(
    (href: string) => {
      setOpen(false)
      // Hash-bearing hrefs (`/reference#tag/...`) need a full URL parse so we
      // can hand react-router a path + hash separately.
      const hashIdx = href.indexOf('#')
      if (hashIdx >= 0) {
        navigate({
          pathname: href.slice(0, hashIdx),
          hash: href.slice(hashIdx),
        })
      } else {
        navigate(href)
      }
    },
    [navigate],
  )

  if (!open) return null

  const hasQuery = query.trim().length > 0

  return (
    <div
      className="search-overlay"
      onClick={() => setOpen(false)}
      role="presentation"
    >
      <div
        className="search-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Search the API docs"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="search-input-row">
          <svg
            className="search-input-icon"
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden
          >
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
            <path
              d="M13.5 13.5L17 17"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
          <input
            ref={inputRef}
            className="search-input"
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder="Search endpoints and guides…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActive((a) => Math.min(a + 1, Math.max(0, hits.length - 1)))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActive((a) => Math.max(a - 1, 0))
              } else if (e.key === 'Enter' && hits[active]) {
                e.preventDefault()
                go(hits[active].entry.href)
              }
            }}
            aria-controls="search-results"
            aria-activedescendant={
              hits[active] ? `search-hit-${active}` : undefined
            }
          />
          <button
            type="button"
            className="search-close"
            aria-label="Close search"
            onClick={() => setOpen(false)}
          >
            Esc
          </button>
        </div>
        <ul id="search-results" className="search-results" role="listbox">
          {!entries && (
            <li className="search-status">Loading search…</li>
          )}
          {hits.map((hit, i) => (
            <li key={hit.excerptKey} role="presentation">
              <button
                id={`search-hit-${i}`}
                type="button"
                role="option"
                aria-selected={i === active}
                className={i === active ? 'search-hit is-active' : 'search-hit'}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(hit.entry.href)}
              >
                <span className="search-hit-row">
                  <span className="search-hit-title">
                    {highlight(hit.entry.title, tokens)}
                  </span>
                  <span
                    className={
                      hit.entry.kind === 'operation'
                        ? 'search-hit-tag is-op'
                        : 'search-hit-tag'
                    }
                  >
                    {hit.entry.kind === 'operation' ? 'API' : 'Guide'}
                  </span>
                </span>
                {hit.entry.subtitle && (
                  <span className="search-hit-excerpt">
                    {highlight(hit.entry.subtitle, tokens)}
                  </span>
                )}
              </button>
            </li>
          ))}
          {entries && hasQuery && hits.length === 0 && (
            <li className="search-status">No results for "{query.trim()}".</li>
          )}
          {entries && !hasQuery && (
            <li className="search-hint">
              Try "authentication", "rate limit", or "candidates".
            </li>
          )}
        </ul>
      </div>
    </div>
  )
}
